
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { X } from 'lucide-react';
import { useLanguage } from '@/components/LanguageContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useMetaPixel } from '@/hooks/useMetaPixel';

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { language } = useLanguage();
  const { sendCookieConsentEvent } = useAnalytics();
  const { sendEvent } = useMetaPixel();

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Pequeno delay para não aparecer imediatamente
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    setIsVisible(false);
    
    // Enviar eventos de consentimento aceito
    sendCookieConsentEvent('accepted');
    sendEvent('ConsentAccepted', {
      content_category: 'consent',
      consent_type: 'cookies'
    });
  };

  const handleDecline = () => {
    localStorage.setItem('cookie-consent', 'declined');
    setIsVisible(false);
    
    // Enviar eventos de consentimento recusado
    sendCookieConsentEvent('declined');
    sendEvent('ConsentDeclined', {
      content_category: 'consent',
      consent_type: 'cookies'
    });
  };

  const handleDismiss = () => {
    // Apenas oculta sem salvar preferência
    setIsVisible(false);
  };

  if (!isVisible) return null;

  const getText = () => {
    switch (language) {
      case 'pt':
        return {
          title: 'Cookies e Privacidade',
          message: 'Utilizamos cookies para melhorar a sua experiência e analisar o tráfego do site. Ao aceitar, concorda com o uso de cookies para analytics e marketing.',
          accept: 'Aceitar',
          decline: 'Recusar',
          privacy: 'Política de Privacidade'
        };
      case 'es':
        return {
          title: 'Cookies y Privacidad',
          message: 'Utilizamos cookies para mejorar tu experiencia y analizar el tráfico del sitio. Al aceptar, aceptas el uso de cookies para analytics y marketing.',
          accept: 'Aceptar',
          decline: 'Rechazar',
          privacy: 'Política de Privacidad'
        };
      case 'fr':
        return {
          title: 'Cookies et Confidentialité',
          message: 'Nous utilisons des cookies pour améliorer votre expérience et analyser le trafic du site. En acceptant, vous acceptez l\'utilisation de cookies pour les analyses et le marketing.',
          accept: 'Accepter',
          decline: 'Refuser',
          privacy: 'Politique de Confidentialité'
        };
      default:
        return {
          title: 'Cookies & Privacy',
          message: 'We use cookies to improve your experience and analyze site traffic. By accepting, you agree to the use of cookies for analytics and marketing.',
          accept: 'Accept',
          decline: 'Decline',
          privacy: 'Privacy Policy'
        };
    }
  };

  const text = getText();

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50">
      <Card className="bg-white dark:bg-gray-800 shadow-lg border-2 border-blue-600">
        <CardContent className="p-4">
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              {text.title}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="h-6 w-6 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            {text.message}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-2 mb-2">
            <Button 
              onClick={handleAccept}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm flex-1"
            >
              {text.accept}
            </Button>
            <Button 
              onClick={handleDecline}
              variant="outline"
              className="text-sm flex-1"
            >
              {text.decline}
            </Button>
          </div>
          
          <div className="text-center">
            <a 
              href={language === 'pt' ? '/pt/privacy' : '/privacy'}
              className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
            >
              {text.privacy}
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
